import { inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { getOrderUuid } from '@app/app.reducer';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store, select } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { GetProductsResponse } from '@products/models/products.model';
import { RedeemVoucherResponse, RedeemedVoucher, ReleaseVoucher, RemoveVoucher } from '@products/models/voucher.model';
import { ContingentBookingService } from '@products/services/contingent-booking.service';
import { LoadProductService } from '@products/services/load-product.service';
import { PreferredTariffValidationService } from '@products/services/preferred-tariff-validation.service';
import { VoucherService } from '@products/services/voucher.service';
import { WorkshopBookingService } from '@products/services/workshop-booking.service';
import { StatusBarService } from '@shared/status-bar/status-bar.service';
import { ProductSelectionActions } from '@store/products/product-selection/product-selection.actions';
import { LoadProductsActions, TariffProductActions, VoucherProductActions } from '@store/products/product.actions';
import { WorkshopActions } from '@store/products/workshop/workshop.actions';
import { EMPTY, catchError, concat, concatMap, filter, forkJoin, map, of, switchMap } from 'rxjs';

export const getProducts$ = createEffect(
  (actions$ = inject(Actions), loadProductService = inject(LoadProductService)) =>
    actions$.pipe(
      ofType(LoadProductsActions.getProducts),
      switchMap(({ eventId, preferredTicketGroupNr, preferredTicketPersonNr }) => {
        if (!eventId) {
          return EMPTY;
        }

        return loadProductService.getProducts$(eventId, preferredTicketGroupNr, preferredTicketPersonNr).pipe(
          map((productsResponse: GetProductsResponse) => {
            const products = loadProductService.mapGetProductResponse(productsResponse);
            return ProductSelectionActions.setInitialProductSelectionList(products);
          }),
          catchError(() => of(ProductSelectionActions.setInitialProductSelectionList({})))
        );
      })
    ),
  { functional: true }
);

export const getPreferredProducts$ = createEffect(
  (actions$ = inject(Actions), loadProductService = inject(LoadProductService)) =>
    actions$.pipe(
      ofType(LoadProductsActions.getPreferredProducts),
      switchMap(({ eventId, preferredTicketGroupNr, preferredTicketPersonNr }) => {
        if (!eventId) {
          return EMPTY;
        }

        return loadProductService.getProducts$(eventId, preferredTicketGroupNr, preferredTicketPersonNr).pipe(
          map((productsResponse: GetProductsResponse) => {
            const products = loadProductService.mapGetProductResponse(productsResponse);
            return ProductSelectionActions.setPreferredProductSelectionList(products);
          }),
          catchError(() => of(ProductSelectionActions.setPreferredProductSelectionList({})))
        );
      })
    ),
  { functional: true }
);

export const getWorkshopProducts$ = createEffect(
  (actions$ = inject(Actions), loadProductService = inject(LoadProductService)) =>
    actions$.pipe(
      ofType(LoadProductsActions.getWorkshopProducts),
      switchMap(({ eventId }) => {
        if (!eventId) {
          return EMPTY;
        }

        return loadProductService.getWorkshopProducts$(eventId).pipe(
          map((workshopProductsResponse) => WorkshopActions.setInitialWorkshopProductList(workshopProductsResponse)),
          catchError(() => of(WorkshopActions.setInitialWorkshopProductList([])))
        );
      })
    ),
  { functional: true }
);

export const setPreferredProduct$ = createEffect(
  (
    actions$ = inject(Actions),
    route = inject(ActivatedRoute),
    preferredTariffValidationService = inject(PreferredTariffValidationService)
  ) =>
    actions$.pipe(
      ofType(ProductSelectionActions.setPreferredProductSelectionList),
      switchMap(() => {
        const { tt, pt, amt } = route.snapshot.queryParams;
        if (!tt) {
          return EMPTY;
        }

        return preferredTariffValidationService.validatePreferredTariffCount$(+tt, +pt, +amt).pipe(
          map(({ ticketTypeId, ticketPersonTypeId, validatedCount }) =>
            TariffProductActions.setPreferredTariffBooking({
              ticketTypeId,
              ticketPersonTypeId,
              count: validatedCount
            })
          )
        );
      })
    ),
  { functional: true }
);

export const redeemVoucher$ = createEffect(
  (
    actions$ = inject(Actions),
    voucherService = inject(VoucherService),
    translateService = inject(TranslateService),
    statusBarService = inject(StatusBarService)
  ) =>
    actions$.pipe(
      ofType(VoucherProductActions.redeemAndReleaseVoucher),
      switchMap(({ redeemVoucher, releaseVouchers, removeProductBookings }) => {
        if (!redeemVoucher) {
          return EMPTY;
        }

        return voucherService.redeemVoucher$(redeemVoucher).pipe(
          switchMap((redeemVoucherResponse: RedeemVoucherResponse) => {
            if (redeemVoucherResponse.allowedWorkshopsFull) {
              return translateService.get('voucher.workshops.soldout').pipe(
                map((voucherWorkshopSoldOutTranslation: string) => ({
                  redeemVoucherResponse,
                  voucherWorkshopSoldOutTranslation
                }))
              );
            }

            return of({ redeemVoucherResponse, voucherWorkshopSoldOutTranslation: '' });
          }),
          map(({ redeemVoucherResponse, voucherWorkshopSoldOutTranslation }) => {
            if (redeemVoucherResponse.allowedWorkshopsFull) {
              statusBarService.setStatus(voucherWorkshopSoldOutTranslation, 'error');
            }

            const { isAnonymous } = redeemVoucher;
            const redeemedVoucher: RedeemedVoucher = !redeemVoucherResponse.allowedWorkshopsFull
              ? {
                  ...redeemVoucherResponse,
                  isAnonymous
                }
              : null;

            return VoucherProductActions.setRedeemedAndReleaseVouchersInProductBookingsAndProductSelection({
              redeemedVoucher,
              releaseVouchers,
              removeProductBookings
            });
          }),
          catchError(() =>
            of(VoucherProductActions.setRedeemedAndReleaseVouchersInProductBookingsAndProductSelection(null))
          )
        );
      })
    ),
  { functional: true }
);

export const releaseVoucher$ = createEffect(
  (actions$ = inject(Actions), voucherService = inject(VoucherService)) =>
    actions$.pipe(
      ofType(VoucherProductActions.releaseVoucher),
      switchMap(({ eventId, orderUuid, voucherCode, countryCode, ticketPersonId, remainingTicketCount }) => {
        if (!eventId || !orderUuid || !voucherCode || !countryCode || !ticketPersonId) {
          return EMPTY;
        }

        const releaseVoucher: ReleaseVoucher = {
          eventId,
          orderUuid,
          voucherCode,
          countryCode,
          ticketPersonId,
          remainingTicketCount
        };
        const removeVoucher: RemoveVoucher = { eventId, orderUuid, ticketPersonId, voucherCode };

        return voucherService.releaseVoucher$(releaseVoucher).pipe(
          map(() => VoucherProductActions.removeReleasedVoucher(removeVoucher)),
          catchError(() => of(VoucherProductActions.removeReleasedVoucher(null)))
        );
      })
    ),
  { functional: true }
);

export const releaseExpiredVoucher$ = createEffect(
  (actions$ = inject(Actions), voucherService = inject(VoucherService)) =>
    actions$.pipe(
      ofType(VoucherProductActions.releaseExpiredVoucher),
      filter(
        ({ eventId, orderUuid, voucherCode, countryCode, ticketPersonId }) =>
          !!eventId && !!orderUuid && !!voucherCode && !!countryCode && !!ticketPersonId
      ),
      concatMap(({ eventId, orderUuid, voucherCode, countryCode, ticketPersonId, remainingTicketCount }) => {
        const removeReleasedVoucher: RemoveVoucher = { eventId, orderUuid, voucherCode, ticketPersonId };

        const removeReleasedVoucherAction$ = of(VoucherProductActions.removeReleasedVoucher(removeReleasedVoucher));

        const releaseVoucher: ReleaseVoucher = { ...removeReleasedVoucher, countryCode, remainingTicketCount };
        const releaseVoucher$ = voucherService.releaseVoucher$(releaseVoucher).pipe(
          switchMap(() => EMPTY),
          catchError(() => EMPTY)
        );

        return concat(removeReleasedVoucherAction$, releaseVoucher$);
      })
    ),
  { functional: true }
);

export const releaseAllContingentReservationsAndWorkshopAssignedSeats$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    contingentBookingService = inject(ContingentBookingService),
    workshopBookingService = inject(WorkshopBookingService)
  ) =>
    actions$.pipe(
      ofType(
        ProductSelectionActions.setInitialProductSelectionList,
        ProductSelectionActions.setPreferredProductSelectionList,
        VoucherProductActions.setRedeemedAndReleaseVouchersInProductBookingsAndProductSelection
      ),
      concatLatestFrom(() => store.pipe(select(getOrderUuid))),
      switchMap(([action, orderUuid]) => {
        if (!action) {
          return EMPTY;
        }

        const isNotAnonymousVoucherRedeemed =
          action.type ===
            VoucherProductActions.setRedeemedAndReleaseVouchersInProductBookingsAndProductSelection.type &&
          !action?.redeemedVoucher?.isAnonymous;

        if (isNotAnonymousVoucherRedeemed) {
          return EMPTY;
        }

        return forkJoin([
          contingentBookingService.postReleaseContingentReservations$(orderUuid),
          workshopBookingService.postReleaseWorkshopBookings$(orderUuid)
        ]).pipe(
          switchMap(() => EMPTY),
          catchError(() => EMPTY)
        );
      })
    ),
  { functional: true, dispatch: false }
);
